import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { Alert, Autocomplete, Box, CircularProgress, TextField } from '@mui/material';
import { ContentModal } from '@localstack/ui';
import { AdminSearchResult } from '@localstack/types';
import { AdminService, useApiGetter } from '@localstack/services';

const SEARCH_DEBOUNCE_RATE = 1000;

export interface AddMemberDialogProps {
  onClose: () => void;
  onConfirm: (userId: string) => void;
}

export const AddMemberDialog = ({ onClose, onConfirm }: AddMemberDialogProps): JSX.Element => {
  const [selectedUser, setSelectedUser] = useState<[string, string]>(['', '']);
  const [usersQuery, setUsersQuery] = useState<Optional<string>>(null);

  const debouncedSetUsersQuery = useMemo(() => debounce(setUsersQuery, SEARCH_DEBOUNCE_RATE), []);
  useEffect(() => () => debouncedSetUsersQuery.cancel(), [debouncedSetUsersQuery]);

  const { data: searchResults, isLoading: isSearching } = useApiGetter(AdminService, 'getSearchResults', [usersQuery], {
    enable: !!usersQuery,
    defaultValue: {} as AdminSearchResult,
  });

  // lets an admin bypass search and add a member by raw ID directly (e.g. if search is down or not yet materialized)
  const augmentedSearchResults: [string, string][] = [
    ...(usersQuery ? [[usersQuery, usersQuery] as [string, string]] : []),
    ...(searchResults.users ?? []),
  ];

  const isManualIdOption = (option: [string, string], index: number) => index === 0 && option[0] === usersQuery;

  return (
    <ContentModal
      open
      modalMaxWidth="sm"
      onClose={onClose}
      fullWidth
      backdropBlurIntensity={3}
      dialogTitle="Add new member to the workspace"
      confirmButtonText="Confirm"
      confirmButtonProps={{ disabled: !selectedUser[0] || !selectedUser[1] }}
      onConfirm={() => onConfirm(selectedUser[0])}
      dialogContent={
        <Autocomplete
          fullWidth
          options={augmentedSearchResults}
          getOptionLabel={(u: [string, string]) => u[1]}
          renderOption={(props, option, state) =>
            isManualIdOption(option, state.index) ? (
              <Box component="li" {...props} sx={{ display: 'block' }}>
                <Alert severity="warning" sx={{ width: '100%' }}>
                  Add by raw ID <code>{option[0]}</code> - only use if you already know this is a valid user ID
                </Alert>
              </Box>
            ) : (
              <Box component="li" {...props}>
                {option[1]}
              </Box>
            )
          }
          onChange={(_, value) => setSelectedUser((value ?? ['', '']) as [string, string])}
          value={selectedUser}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Member"
              variant="outlined"
              size="small"
              onChange={(e: ChangeEvent<HTMLInputElement>) => debouncedSetUsersQuery(e.target.value)}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isSearching ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      }
    />
  );
};
